import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import { type FC, useCallback, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import type { SoftReceivedGoodsReceiptFormValues } from "../../schemas"
import { softReceivedGoodsReceiptFormSchema } from "../../schemas"
import { getSoftReceivedGoodsReceiptDefaultFormValues, serializeSoftReceivedGoodsReceiptForm } from "../../utils"

import { useUpdateGoodsReceipt } from "./../../../../hooks"
import { SoftReceivedGoodsReceiptForm } from "./SoftReceivedGoodsReceiptForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"

interface SoftReceivedGoodsReceiptFormDialogProps {
  goodsReceipt: GoodsReceipt
  onClose: () => void
  onRefetch: () => void
}

export const SoftReceivedGoodsReceiptFormDialog: FC<SoftReceivedGoodsReceiptFormDialogProps> = ({
  goodsReceipt,
  onClose,
  onRefetch,
}) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const { update, isUpdating } = useUpdateGoodsReceipt({
    onSuccess: onRefetch,
    successMessage: "Goods Receipt successfully updated.",
  })

  const methods = useForm<SoftReceivedGoodsReceiptFormValues>({
    resolver: zodResolver(softReceivedGoodsReceiptFormSchema),
    defaultValues: getSoftReceivedGoodsReceiptDefaultFormValues(goodsReceipt),
    mode: "all",
  })

  const {
    handleSubmit,
    formState: { isValid, isDirty },
  } = methods

  const canSubmit = isValid && isDirty && !isUpdating

  const handleClose = useCallback(() => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }, [isDirty, onClose])

  const handleCancelDialogClose = () => setIsCancelDialogOpen(false)

  const handleCancelConfirm = () => {
    onClose()
  }

  const submitForm = async (formValues: SoftReceivedGoodsReceiptFormValues) => {
    try {
      const payload = serializeSoftReceivedGoodsReceiptForm(goodsReceipt!, formValues)
      await update(payload)

      onClose()
    } catch (error) {
      console.error("Error updating soft received goods receipt:", error)
    }
  }

  return isCancelDialogOpen ? (
    <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleCancelConfirm} />
  ) : (
    <Dialog open onClose={handleClose} maxWidth='sm' fullWidth data-testid='soft-received-form-dialog'>
      <DialogHeader title='Edit Goods Receipt' onClose={handleClose} />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitForm)}>
          <DialogContent className='pt-4'>
            <SoftReceivedGoodsReceiptForm />
          </DialogContent>

          <DialogActions className='px-6 pb-6'>
            <Button appearance='outlined' onClick={handleClose} disabled={isUpdating} data-testid='cancel-button'>
              Cancel
            </Button>
            <Button
              type='submit'
              appearance='contained'
              disabled={!canSubmit}
              loading={isUpdating}
              data-testid='save-button'
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
